import { ICommandBarItemProps, IContextualMenuItem, Panel, PanelType } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import AppContext from 'App/AppContext';
import {
  IPCallout,
  IPCheckbox,
  IPCommandBar,
  IPDefaultButton,
  IPDropdown,
  IPMessageBar,
  IPMessageBarType,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPTextField,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AICriterionOutcomeDetails from 'components/AuditCriteria/AICriterionOutcomeDetails';
import AssessmentStartCard from 'components/AuditCriteria/AssessmentStartCard';
import EvidenceTaskSection from 'components/AuditCriteria/EvidenceTaskSection';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import GenericModal from 'components/Dialogs/GenericModal';
import NormMultiSelectDropdown, { INormOption } from 'components/Norm/NormMultiSelectDropdown';
import { commentIcon, deleteIcon, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import AuditCriteriaSet, {
  AuditCriterion,
  auditCriterionModeOneTime,
  auditCriterionModeRecurring,
  auditCriterionStateActive,
  auditCriterionStateArchived,
  auditCriterionStateNotApplicable,
  auditCriterionVerificationAI,
  auditCriterionVerificationManual,
  EvidenceTaskBinding,
  EvidenceTaskBindingRow,
  getAuditCriterionStateText,
  normalizeAuditEntityType,
} from 'models/auditCriteria';
import { AICriterionOutcomeResult, AICriterionOutcomeStatus } from 'models/auditCriteriaGeneric';
import { EntityTypes } from 'models/entity';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import Norm from 'models/norm';
import { AIEnabled } from 'models/setting';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getOutcomeSourceDisplayName } from './automationNames';
import {
  apiDismissCriterionOutcome,
  apiGetAuditCriteria,
  apiSaveAuditCriteria,
  apiSetAuditCriteriaState,
} from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { toLocaleDateShort } from 'utils/datetime';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

interface IAuditCriterionPanelProps {
  isOpen: boolean;
  // Shell: a Fluent panel over a plain host, a modal when the host is itself a modal (the procedure
  // form editor and the criteria editor both are, so in practice this opens as a modal there -
  // opening a panel over an active modal is weird UX). Same dual shape the KPI FieldPanel has.
  asPanel?: boolean;
  entityType: number;
  entityId: number;
  // The stored set this criterion belongs to; undefined when the entity has none yet. Saving is a
  // whole-set PUT (the server forks/retires/re-keys and re-projects the covering procedure forms).
  set: AuditCriteriaSet | undefined;
  // The criterion to edit; undefined adds a new one.
  criterionKey?: string;
  readonly?: boolean;
  // The server round-trip result after any write, so the host can refresh its view of the set AND
  // reload the projected form.
  onSaved: (set: AuditCriteriaSet) => void;
  onDismiss: () => void;
}

// The key the outcomes of a criterion are kept under: its lineage, the key of its first version.
const getLineageKey = (criterion: AuditCriterion): string => criterion.lineageKey || criterion.criterionKey;

// A manual key that is free: a plain counter collided with a stored key as soon as a criterion had
// been removed, and a key IS the criterion's identity.
const getNewCriterionKey = (criteria: AuditCriterion[]): string => {
  const used = new Set(criteria.map((criterion) => criterion.criterionKey));
  let number = criteria.length + 1;
  while (used.has(`manual.criterion.${number}`)) {
    number++;
  }

  return `manual.criterion.${number}`;
};

const AuditCriterionPanel = (props: IAuditCriterionPanelProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  // PLAN-075's parts of this component are a preview (isAuditCriteriaPreviewEnabled)
  const auditPreview = isAuditCriteriaPreviewEnabled(appContext);

  const [criterion, setCriterion] = useState<AuditCriterion>(() => new AuditCriterion());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  // the comments and history, collapsed unless the person left them open last time (PLAN-CTI-082)
  const [isTrailOpen, setIsTrailOpen] = useState<boolean>(
    () => getLocalStorageData(appContext, LocalStorageKeys.AuditCriterionTrailOpen) === 'true',
  );
  const [commentCount, setCommentCount] = useState<number>(0);
  // seeded from the synchronous cache so the standards are known when the panel opens; refreshed below
  const [norms, setNorms] = useState<Norm[]>(() => appContext.globalDataCache.norms.items);
  // the non-control families are standard-agnostic unless narrowed by hand: the checkbox state
  const [narrowStandards, setNarrowStandards] = useState<boolean>(false);

  const stored = useMemo(
    () => props.set?.criteria.find((item) => item.criterionKey === props.criterionKey),
    [props.set, props.criterionKey],
  );
  const isNew = !stored;
  // Only an active criterion is edited; a not applicable or archived one is shown read-only until a
  // person moves it back to Active.
  const storedState = stored?.state ?? auditCriterionStateActive;
  const isActive = storedState === auditCriterionStateActive;
  const isSaved = !!stored;
  // A control criterion belongs to at least one of the control's linked standards (a requirement folds
  // to the control family). A risk, process, asset, objective or processing-activity criterion applies
  // under every standard unless narrowed by hand.
  const isControlFamily = normalizeAuditEntityType(props.entityType) === (EntityTypes.Control as number);

  // The standards a control criterion is offered: the ISO standards whose requirements the control
  // implements (ControlISOControl), never the custom standards it is linked to. A control that
  // implements no requirement falls back to every active ISO standard, so a criterion can still be
  // written for it.
  const isIsoNorm = (normId: number): boolean => {
    const norm = norms.find((item) => item.normId === normId);

    return norm?.isoNormId !== undefined && norm.isoNormId > 0;
  };
  const linkedIsoNormIds = (props.set?.linkedNormIds ?? []).filter(isIsoNorm);
  const activeNormIds = norms.filter((norm) => norm.active).map((norm) => norm.normId);
  const activeIsoNormIds = activeNormIds.filter(isIsoNorm);

  useEffect(() => {
    let cancelled = false;
    void appContext.globalDataCache.norms.getItems().then((items) => {
      if (!cancelled) setNorms(items);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The license AND the tenant switch, the gate the criteria editor puts on its AI commands. It hides
  // the AI verification for NEW criteria and the stored advice; a criterion already verified by the AI
  // keeps showing that, so switching AI off never flips it silently.
  const aiEnabled =
    hasUserFeature(appContext, FeatureTypes.AIAssistant) &&
    (appContext.globalDataCache.settings.get(AIEnabled) as boolean) === true;

  useEffect(() => {
    if (!props.isOpen) return;
    const next = stored ? stored.clone() : new AuditCriterion();
    if (!stored && !aiEnabled) {
      next.verification = auditCriterionVerificationManual;
    }
    // a new control criterion starts with every ISO standard the control implements a requirement of;
    // a stored one shows what it carries
    if (!stored && isControlFamily) {
      next.normIds = [...linkedIsoNormIds];
    }
    setNarrowStandards((stored?.normIds ?? []).length > 0);

    setCriterion(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.criterionKey, aiEnabled]);

  const update = (change: (next: AuditCriterion) => void) => {
    const next = criterion.clone();
    change(next);
    setCriterion(next);
  };

  const [showAssess, setShowAssess] = useState<boolean>(false);
  // "Open the result": the latest outcome's details beside its bar
  const [showOutcomeResult, setShowOutcomeResult] = useState<boolean>(false);
  const outcomeResultButtonId = useId('criterion-outcome-result');

  const criteriaChanged = stored ? !criterion.isEqual(stored) : true;
  const hasChanges = criteriaChanged;

  // The latest AI outcome of the criterion that still stands (not dismissed or superseded), spec 5.7.
  const latestOutcome =
    auditPreview && stored
      ? props.set?.latestOutcomes.find(
          (outcome) =>
            outcome.lineageKey.toLowerCase() === getLineageKey(stored).toLowerCase() &&
            outcome.status !== AICriterionOutcomeStatus.Dismissed &&
            outcome.status !== AICriterionOutcomeStatus.Superseded,
        )
      : undefined;

  // The standards field. Selected: the explicit standards, or for a legacy control criterion (none
  // stored) the effective ones the server computed. Options: for a control the ISO standards it
  // implements (see above), for the other families every active standard - plus whatever the
  // criterion already carries, so a stale or hidden standard stays visible and removable instead of
  // being dropped by a save.
  const selectedNormIds = criterion.normIds ?? (isControlFamily ? criterion.effectiveNormIds : []);
  const offeredNormIds = isControlFamily
    ? linkedIsoNormIds.length > 0
      ? linkedIsoNormIds
      : activeIsoNormIds
    : activeNormIds;
  const standardOptions: INormOption[] = [...new Set([...offeredNormIds, ...selectedNormIds])].map((normId) => {
    const norm = norms.find((item) => item.normId === normId);

    return { normId, name: norm?.name ?? t('criteria:Fields.RestrictedStandard', { id: normId }) };
  });
  const isValid =
    criterion.requirement.trim().length > 0 &&
    criterion.successCriteria.trim().length > 0 &&
    (!isControlFamily || selectedNormIds.length > 0);
  const saveDisabled = props.readonly || !isActive || isSaving || !isValid || !hasChanges;

  const buildNextCriteria = (mutate: (list: AuditCriterion[]) => AuditCriterion[]): AuditCriterion[] => {
    const current = (props.set?.criteria ?? []).map((item) => item.clone());

    return mutate(current);
  };

  const putCriteria = async (next: AuditCriterion[]): Promise<AuditCriteriaSet | undefined> => {
    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiSaveAuditCriteria(props.entityType, props.entityId, next, accessToken);
      props.onSaved(result);

      return result;
    } catch (err) {
      appContext.setError(err);

      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  const save = async () => {
    const toSave = criterion.clone();
    if (!toSave.criterionKey) {
      toSave.criterionKey = getNewCriterionKey(props.set?.criteria ?? []);
    }

    let result = props.set;
    if (criteriaChanged) {
      const next = buildNextCriteria((list) => {
        const index = list.findIndex((item) => item.criterionKey === props.criterionKey);
        if (index >= 0) {
          list[index] = toSave;
        } else {
          list.push(toSave);
        }

        return list;
      });

      result = await putCriteria(next);
      if (!result) return;
    }

    props.onDismiss();
  };

  const dismissOutcome = async () => {
    if (!latestOutcome) return;

    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDismissCriterionOutcome(latestOutcome.aiCriterionOutcomeId, accessToken);
      appContext.showNotification(t('criteria:Generic.Outcome.Dismissed'), false, false, true);
      props.onSaved(await apiGetAuditCriteria(props.entityType, props.entityId, accessToken));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const getOutcomeResultText = (result: AICriterionOutcomeResult): string => {
    switch (result) {
      case AICriterionOutcomeResult.Pass:
        return t('criteria:Generic.Outcome.Pass');
      case AICriterionOutcomeResult.Fail:
        return t('criteria:Generic.Outcome.Fail');
      default:
        return t('criteria:Generic.Outcome.Inconclusive');
    }
  };

  const deleteCriterion = async () => {
    setShowDeleteDialog(false);
    if (!stored) return;

    // Removing the last criterion posts an empty list, which clears the whole set server-side:
    // "no criteria" again, not an empty reviewed set.
    const next = buildNextCriteria((list) => list.filter((item) => item.criterionKey !== props.criterionKey));
    const result = await putCriteria(next);
    if (result) props.onDismiss();
  };

  // The state is its own write, never part of a save: the server stamps who changed it and records it
  // on the criterion's audit trail.
  const changeState = async (state: string) => {
    if (!stored) return;

    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiSetAuditCriteriaState(props.entityType, props.entityId, [stored.criterionKey], state, accessToken);
      props.onSaved(result);
      props.onDismiss();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsSaving(false);
    }
  };

  // The transitions a person may make: Active to and from Not applicable, Active to and from Archived.
  // The current state is shown checked and disabled, a state it cannot reach disabled.
  const canMoveTo = (state: string): boolean =>
    state !== storedState && (storedState === auditCriterionStateActive || state === auditCriterionStateActive);

  const stateMenuItems: IContextualMenuItem[] = [
    auditCriterionStateActive,
    auditCriterionStateNotApplicable,
    auditCriterionStateArchived,
  ].map((state) => ({
    key: state,
    text: getAuditCriterionStateText(state, t),
    canCheck: true,
    checked: state === storedState,
    disabled: !canMoveTo(state),
    onClick: () => void changeState(state),
  }));

  //
  // Evidence tasks: a ONE-TIME criterion binds the task that holds its proof. Bindable only once
  // the criterion is saved - the server binds against the stored set.
  //
  const refreshAfterBindingChange = async (rows: EvidenceTaskBindingRow[]) => {
    // Rows carry no task state or AI advice; both show again on the next load of the set.
    const bindings = rows
      .filter((row) => row.criterionKey.toLowerCase() === criterion.criterionKey.toLowerCase())
      .map((row) => {
        const binding = new EvidenceTaskBinding();
        binding.taskId = row.taskId;
        binding.taskName = row.taskName;
        binding.mode = row.mode;
        binding.hidden = row.hidden;
        binding.criterionCount = row.criterionCount;

        return binding;
      });

    update((next) => {
      next.evidenceTasks = bindings;
    });

    if (props.set) {
      const set = props.set.clone();
      const target = set.criteria.find((item) => item.criterionKey === criterion.criterionKey);
      if (target) target.evidenceTasks = bindings.map((binding) => binding.clone());
      props.onSaved(set);
    }
  };

  // After a task was worked on in its modal: the whole set again, because task state and AI advice
  // (completed, judged by the evidence assistant) only travel with the set, never with binding rows.
  const reloadSet = async () => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiGetAuditCriteria(props.entityType, props.entityId, accessToken);
      const target = set.criteria.find((item) => item.criterionKey === criterion.criterionKey);
      if (target) {
        update((next) => {
          next.evidenceTasks = target.evidenceTasks.map((binding) => binding.clone());
        });
      }
      props.onSaved(set);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const modeOptions: IPSelectableOption[] = [
    { key: auditCriterionModeRecurring, text: t('criteria:Mode.Recurring') },
    { key: auditCriterionModeOneTime, text: t('criteria:Mode.OneTime') },
  ];

  const verificationOptions: IPSelectableOption[] = [
    ...(aiEnabled || stored?.verification === auditCriterionVerificationAI
      ? [{ key: auditCriterionVerificationAI, text: t('criteria:Verification.AI') }]
      : []),
    { key: auditCriterionVerificationManual, text: t('criteria:Verification.Manual') },
  ];

  const getSourceKindText = (): string | undefined => {
    switch (criterion.sourceKind) {
      case 'Document':
        return t('criteria:Source.Document');
      case 'Control':
        return t('criteria:Source.Control');
      case 'Implementation':
        return t('criteria:Source.Implementation');
      case 'Standard':
        return t('criteria:Source.Standard');
      default:
        return criterion.generatedOn ? undefined : t('criteria:Source.Manual');
    }
  };

  const disabled = props.readonly || !isActive || isSaving;

  const body = (
    <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 8 } }}>
      {storedState === auditCriterionStateArchived && (
        <IPStack.Item>
          <IPMessageBar messageBarType={IPMessageBarType.warning}>{t('criteria:Archived')}</IPMessageBar>
        </IPStack.Item>
      )}
      {storedState === auditCriterionStateNotApplicable && (
        <IPStack.Item>
          <IPMessageBar messageBarType={IPMessageBarType.info}>{t('criteria:State.NotApplicableInfo')}</IPMessageBar>
        </IPStack.Item>
      )}
      <IPStack.Item>
        <IPTextField
          label={t('criteria:Fields.Requirement')}
          multiline
          rows={3}
          required
          value={criterion.requirement}
          disabled={disabled}
          onChange={(_, value) => update((next) => (next.requirement = value ?? ''))}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPTextField
          label={t('criteria:Fields.ColumnName')}
          description={t('criteria:Fields.ColumnNameInfo')}
          value={criterion.columnName}
          maxLength={64}
          disabled={disabled}
          onChange={(_, value) => update((next) => (next.columnName = value ?? ''))}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPTextField
          label={t('criteria:Fields.SuccessCriteria')}
          multiline
          rows={3}
          required
          value={criterion.successCriteria}
          disabled={disabled}
          onChange={(_, value) => update((next) => (next.successCriteria = value ?? ''))}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapMedium}>
          <IPStack.Item grow>
            <IPDropdown
              label={t('criteria:Fields.Mode')}
              info={t('criteria:Fields.ModeInfo')}
              options={modeOptions}
              selectedKey={criterion.mode}
              disabled={disabled}
              onChange={(_, option) => update((next) => (next.mode = (option?.key as string) ?? next.mode))}
            />
          </IPStack.Item>
          <IPStack.Item grow>
            <IPDropdown
              label={t('criteria:Fields.Verification')}
              options={verificationOptions}
              selectedKey={criterion.verification}
              disabled={disabled}
              onChange={(_, option) =>
                update((next) => (next.verification = (option?.key as string) ?? next.verification))
              }
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        {isControlFamily ? (
          <NormMultiSelectDropdown
            label={t('criteria:Fields.Standards')}
            info={t('criteria:Fields.StandardsInfo')}
            options={standardOptions}
            selectedNormIds={selectedNormIds}
            disabled={disabled}
            errorMessage={selectedNormIds.length === 0 ? t('criteria:Fields.StandardsRequired') : undefined}
            onChange={(normIds) => update((next) => (next.normIds = normIds))}
          />
        ) : (
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPCheckbox
              label={t('criteria:Fields.AllStandards')}
              checked={!narrowStandards}
              disabled={disabled}
              onChange={(_, checked) => {
                setNarrowStandards(checked !== true);
                if (checked) update((next) => (next.normIds = undefined));
              }}
            />
            {narrowStandards && (
              <NormMultiSelectDropdown
                label={t('criteria:Fields.Standards')}
                info={t('criteria:Fields.StandardsInfo')}
                options={standardOptions}
                selectedNormIds={selectedNormIds}
                disabled={disabled}
                onChange={(normIds) => update((next) => (next.normIds = normIds))}
              />
            )}
          </IPStack>
        )}
      </IPStack.Item>
      <IPStack.Item>
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPCheckbox
            label={t('criteria:Fields.Required')}
            checked={criterion.required}
            disabled={disabled}
            onChange={(_, checked) => update((next) => (next.required = checked === true))}
          />
          <IPCheckbox
            label={t('criteria:Fields.RequiresComment')}
            checked={criterion.requiresComment}
            disabled={disabled}
            onChange={(_, checked) => update((next) => (next.requiresComment = checked === true))}
          />
          <IPCheckbox
            label={t('criteria:Fields.RequiresEvidence')}
            checked={criterion.requiresEvidence}
            disabled={disabled}
            onChange={(_, checked) => update((next) => (next.requiresEvidence = checked === true))}
          />
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPTextField
          label={t('criteria:Fields.ExternalId')}
          description={t('criteria:Fields.ExternalIdInfo')}
          value={criterion.externalId}
          maxLength={256}
          disabled={disabled}
          onChange={(_, value) => update((next) => (next.externalId = value ?? ''))}
        />
      </IPStack.Item>
      {latestOutcome && (
        <IPStack.Item>
          <IPMessageBar
            messageBarType={IPMessageBarType.info}
            isMultiline={true}
            actions={
              props.readonly ? undefined : (
                <IPStack horizontal tokens={globalStackTokensGapSmall}>
                  <IPDefaultButton
                    id={outcomeResultButtonId}
                    text={t('criteria:Generic.Outcome.OpenResult')}
                    onClick={() => setShowOutcomeResult((current) => !current)}
                  />
                  {props.entityId > 0 && (
                    <IPDefaultButton
                      text={t('criteria:Generic.Outcome.StartAssessment')}
                      disabled={isSaving}
                      onClick={() => setShowAssess(true)}
                    />
                  )}
                  <IPDefaultButton
                    text={t('criteria:Generic.Outcome.Dismiss')}
                    disabled={isSaving}
                    onClick={() => void dismissOutcome()}
                  />
                </IPStack>
              )
            }
          >
            <IPText block>
              {t('criteria:Generic.Outcome.Available', {
                result: getOutcomeResultText(latestOutcome.proposedResult),
                source: getOutcomeSourceDisplayName(
                  latestOutcome.sourceKind,
                  latestOutcome.sourceRef,
                  latestOutcome.sourceName,
                  t as TFunction<string[]>,
                ),
                date: toLocaleDateShort(latestOutcome.created),
              })}
            </IPText>
            {latestOutcome.summary && <IPText block>{latestOutcome.summary}</IPText>}
          </IPMessageBar>
        </IPStack.Item>
      )}
      {criterion.mode === auditCriterionModeOneTime && isActive && (
        <IPStack.Item>
          <IPSeparator />
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText styles={{ root: { fontWeight: 600 } }}>{t('criteria:Tasks.Label')}</IPText>
            {!isSaved && <IPText variant="small">{t('criteria:Tasks.SaveFirst')}</IPText>}
            {isSaved && (
              <EvidenceTaskSection
                entityType={props.entityType}
                entityId={props.entityId}
                criterionKey={criterion.criterionKey}
                criterionRequirement={criterion.requirement}
                bindings={criterion.evidenceTasks}
                readonly={props.readonly}
                aiEnabled={aiEnabled}
                onBindingsChanged={refreshAfterBindingChange}
                onTasksChanged={reloadSet}
              />
            )}
          </IPStack>
        </IPStack.Item>
      )}
      {(getSourceKindText() || criterion.sourceText || criterion.generatedOn) && (
        <IPStack.Item>
          <IPSeparator />
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText styles={{ root: { fontWeight: 600 } }}>{t('criteria:Provenance.Title')}</IPText>
            {getSourceKindText() && (
              <IPText variant="small">
                {t('criteria:Provenance.Source')}: {getSourceKindText()}
                {criterion.sourceName ? ` - ${criterion.sourceName}` : ''}
              </IPText>
            )}
            {criterion.sourceText && <IPText variant="small">{criterion.sourceText}</IPText>}
            {criterion.conflictNote && (
              <IPMessageBar messageBarType={IPMessageBarType.warning}>{criterion.conflictNote}</IPMessageBar>
            )}
            {criterion.generatedOn && (
              <IPText variant="small">
                {t('criteria:Provenance.GeneratedOn', { date: toLocaleDateShort(criterion.generatedOn) })}
              </IPText>
            )}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );

  // The criterion's comments and change history, shown while the person has them open. The trail scrolls
  // inside its box, so the box gets a height: a fixed one under the form (the panel), the column's
  // height beside it (the modal).
  const hasTrail = !!stored && stored.auditCriterionId > 0;
  const showTrail = hasTrail && isTrailOpen;
  const renderActivity = (beside: boolean) =>
    stored && showTrail ? (
      <IPStack
        tokens={globalStackTokensGapSmall}
        verticalFill={beside}
        styles={{ root: beside ? { height: '100%', minHeight: 0 } : { paddingTop: 12 } }}
      >
        {!beside && <IPSeparator />}
        <IPText styles={{ root: { fontWeight: 600 } }}>{t('criteria:Activity.Title')}</IPText>
        <IPStack.Item
          grow={beside}
          styles={{ root: beside ? { minHeight: 0, position: 'relative' } : { height: 360, position: 'relative' } }}
        >
          <ActivityTrail
            commentTrailId={stored.commentTrailId}
            auditTrailId={stored.auditTrailId}
            auditCriterionId={stored.auditCriterionId}
            authSchemaIds={[]}
            readOnly={props.readonly}
            onCommentCountChange={setCommentCount}
          />
        </IPStack.Item>
      </IPStack>
    ) : undefined;

  const toggleTrail = () => {
    const next = !isTrailOpen;
    setLocalStorageData(appContext, LocalStorageKeys.AuditCriterionTrailOpen, next ? 'true' : 'false');
    setIsTrailOpen(next);
  };

  // Saving is what this editor is for, so it owns the footer (Save + Cancel, the shape every other modal
  // has). The state, deleting and the comments act on the STORED criterion instead of on what is being
  // edited, so they are the command bar above the form; a new criterion has none.
  const canSave = !props.readonly && isActive;

  const commandBarItems: ICommandBarItemProps[] =
    isSaved && !props.readonly
      ? [
          {
            key: 'state',
            text: t('criteria:State.Command', { state: getAuditCriterionStateText(storedState, t) }),
            disabled: isSaving || hasChanges,
            subMenuProps: { items: stateMenuItems },
          },
          ...(isActive
            ? [
                {
                  key: 'delete',
                  text: t('criteria:Commands.Delete'),
                  iconProps: deleteIcon,
                  disabled: isSaving,
                  onClick: () => setShowDeleteDialog(true),
                },
              ]
            : []),
        ]
      : [];
  const commandBarFarItems: ICommandBarItemProps[] = hasTrail
    ? [
        {
          key: 'comments',
          text:
            commentCount > 0
              ? t('criteria:Commands.CommentsCount', { n: commentCount })
              : t('criteria:Commands.Comments'),
          iconProps: commentIcon,
          canCheck: true,
          checked: isTrailOpen,
          onClick: toggleTrail,
        },
      ]
    : [];
  const commandBar =
    commandBarItems.length > 0 || commandBarFarItems.length > 0 ? (
      <IPCommandBar items={commandBarItems} farItems={commandBarFarItems} styles={{ root: { padding: 0 } }} />
    ) : undefined;

  const dialogs = (
    <>
      <DialogConfirmDelete
        hidden={!showDeleteDialog}
        title={t('criteria:DeleteConfirmTitle')}
        subText={t('criteria:DeleteConfirm')}
        onYes={() => void deleteCriterion()}
        onNo={() => setShowDeleteDialog(false)}
      />
      {showOutcomeResult && latestOutcome && (
        <IPCallout
          target={`#${outcomeResultButtonId}`}
          onDismiss={() => setShowOutcomeResult(false)}
          styles={{ calloutMain: { padding: 12, maxHeight: '50vh', overflowY: 'auto' } }}
        >
          <AICriterionOutcomeDetails outcome={latestOutcome} />
        </IPCallout>
      )}
      {showAssess && (
        <AssessmentStartCard
          entityType={props.entityType}
          entityIds={[props.entityId]}
          onClose={() => setShowAssess(false)}
        />
      )}
    </>
  );

  if (props.asPanel) {
    return (
      <>
        <Panel
          isOpen={props.isOpen}
          onDismiss={props.onDismiss}
          type={PanelType.medium}
          headerText={isNew ? t('criteria:TitleAdd') : t('criteria:TitleEdit')}
          isFooterAtBottom
          onRenderFooterContent={() => (
            <IPStack horizontal horizontalAlign="end" tokens={globalStackTokensGapSmall}>
              {canSave && (
                <IPPrimaryButton text={t('criteria:Save')} disabled={saveDisabled} onClick={() => void save()} />
              )}
              <IPDefaultButton
                text={canSave ? t('criteria:Cancel') : t('criteria:Close')}
                disabled={isSaving}
                onClick={props.onDismiss}
              />
            </IPStack>
          )}
        >
          {commandBar}
          {body}
          {renderActivity(false)}
        </Panel>
        {dialogs}
      </>
    );
  }

  return (
    <>
      <GenericModal
        isOpen={props.isOpen}
        isBlocking
        onClose={props.onDismiss}
        title={isNew ? t('criteria:TitleAdd') : t('criteria:TitleEdit')}
        width={1000}
        maxWidth={'min(95vw, 1000px)'}
        height={'90vh'}
        maxHeight={'90vh'}
        isActionPending={isSaving}
        //save() dismisses itself once the server accepted the set, so a failed save keeps the editor
        //open with the edit still in it
        onSave={canSave ? () => void save() : undefined}
        keepOpenAfterSave
        saveButtonText={t('criteria:Save')}
        saveDisabled={saveDisabled}
        closeButtonText={canSave ? t('criteria:Cancel') : t('criteria:Close')}
      >
        <IPSeparator />
        {commandBar}
        {/* the form scrolls, the title, the command bar and the footer stay put: reserve the modal's own
            chrome so only the form (and the trail beside it) get a scrollbar and never the modal as a whole */}
        <IPStack
          horizontal
          tokens={globalStackTokensGapMedium}
          styles={{ root: { height: commandBar ? 'calc(90vh - 225px)' : 'calc(90vh - 180px)', minHeight: 0 } }}
        >
          <IPStack.Item grow styles={{ root: { minWidth: 0, overflowY: 'auto', overflowX: 'hidden' } }}>
            {body}
          </IPStack.Item>
          {showTrail && (
            <IPStack.Item disableShrink styles={{ root: { width: 380, minHeight: 0 } }}>
              {renderActivity(true)}
            </IPStack.Item>
          )}
        </IPStack>
      </GenericModal>
      {dialogs}
    </>
  );
};

export default AuditCriterionPanel;
